/**
 * Alshallal Pastry Company HR System - Branches Admin Page
 * Phase 24.8: Branch Management
 * 
 * RBAC: HR Manager / HR Admin / Super Admin only
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Users,
  Loader2,
  AlertCircle,
  MapPin
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BranchesPage = () => {
  const { token, hasRole } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  // State
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog state
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, branch: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, branch: null });
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: ''
  });

  // RBAC check - HR Manager, HR Admin, Super Admin
  const canManage = hasRole('hr_manager', 'hr_admin', 'super_admin');

  // Fetch branches
  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_URL}/branches/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBranches(response.data.branches || []);
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (canManage) {
      fetchBranches();
    }
  }, [fetchBranches, canManage]);

  // Create branch
  const handleCreate = async () => {
    if (!formData.name_en.trim()) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      await axios.post(`${API_URL}/branches/`, {
        name_en: formData.name_en.trim(),
        name_ar: formData.name_ar.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreateDialog(false);
      setFormData({ name_en: '', name_ar: '' });
      fetchBranches();
    } catch (err) {
      setError(err.response?.data?.detail?.error || err.response?.data?.detail || 'Failed to create branch');
    } finally {
      setSubmitting(false);
    }
  };

  // Update branch
  const handleUpdate = async () => {
    if (!formData.name_en.trim()) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      await axios.patch(`${API_URL}/branches/${editDialog.branch.id}`, {
        name_en: formData.name_en.trim(),
        name_ar: formData.name_ar.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditDialog({ open: false, branch: null });
      setFormData({ name_en: '', name_ar: '' });
      fetchBranches();
    } catch (err) {
      setError(err.response?.data?.detail?.error || err.response?.data?.detail || 'Failed to update branch');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete branch
  const handleDelete = async () => {
    if (!deleteDialog.branch) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      await axios.delete(`${API_URL}/branches/${deleteDialog.branch.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeleteDialog({ open: false, branch: null });
      fetchBranches();
    } catch (err) {
      setError(err.response?.data?.detail?.error || err.response?.data?.detail || 'Failed to delete branch');
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (branch) => {
    setFormData({
      name_en: branch.name_en || '',
      name_ar: branch.name_ar || ''
    });
    setEditDialog({ open: true, branch });
  };

  // Access denied
  if (!canManage) {
    return (
      <div className="p-6" data-testid="branches-access-denied">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-900">
              {isRTL ? 'الوصول مرفوض' : 'Access Denied'}
            </h2>
            <p className="text-slate-500 mt-2">
              {isRTL 
                ? 'فقط مدير الموارد البشرية والمشرف العام يمكنهم إدارة الفروع.'
                : 'Only HR Manager, HR Admin, and Super Admin can manage branches.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("p-6 space-y-6", isRTL && "rtl")} data-testid="branches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="h-7 w-7 text-purple-600" />
            {isRTL ? 'إدارة الفروع' : 'Branch Management'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isRTL ? 'إنشاء وإدارة فروع المؤسسة' : 'Create and manage company branches'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchBranches} data-testid="refresh-branches-btn">
            <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
            {isRTL ? 'تحديث' : 'Refresh'}
          </Button>
          <Button onClick={() => setCreateDialog(true)} data-testid="create-branch-btn">
            <Plus className="h-4 w-4 me-2" />
            {isRTL ? 'فرع جديد' : 'New Branch'}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700" data-testid="branches-error">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Stats Card */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-purple-600" />
              <span className="text-slate-600">
                {isRTL ? 'إجمالي الفروع' : 'Total Branches'}
              </span>
            </div>
            <Badge variant="secondary" data-testid="branches-count">
              {branches.length} {isRTL ? 'فروع' : 'branches'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : branches.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="branches-empty">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>{isRTL ? 'لا توجد فروع' : 'No branches found'}</p>
              <Button className="mt-4" onClick={() => setCreateDialog(true)}>
                <Plus className="h-4 w-4 me-2" />
                {isRTL ? 'إنشاء فرع' : 'Create Branch'}
              </Button>
            </div>
          ) : (
            <Table data-testid="branches-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? 'الاسم (إنجليزي)' : 'Name (English)'}</TableHead>
                  <TableHead>{isRTL ? 'الاسم (عربي)' : 'Name (Arabic)'}</TableHead>
                  <TableHead>{isRTL ? 'الموظفون' : 'Employees'}</TableHead>
                  <TableHead>{isRTL ? 'تاريخ الإنشاء' : 'Created'}</TableHead>
                  <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branches.map((branch) => (
                  <TableRow key={branch.id} data-testid={`branch-row-${branch.id}`}>
                    <TableCell className="font-medium">{branch.name_en || '-'}</TableCell>
                    <TableCell>{branch.name_ar || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-slate-400" />
                        {branch.employee_count || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      {branch.created_at 
                        ? new Date(branch.created_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell className="text-end">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(branch)}
                          data-testid={`edit-branch-${branch.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteDialog({ open: true, branch })}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-branch-${branch.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md" data-testid="create-branch-dialog">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'إنشاء فرع جديد' : 'Create New Branch'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'أضف فرعًا جديدًا للمؤسسة' : 'Add a new branch to the organization'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (إنجليزي) *' : 'Name (English) *'}</Label>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                placeholder={isRTL ? 'مثال: فرع الرياض' : 'e.g. Riyadh Branch'}
                data-testid="input-branch-name-en"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (عربي)' : 'Name (Arabic)'}</Label>
              <Input
                value={formData.name_ar}
                onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                placeholder="مثال: فرع الرياض"
                dir="rtl"
                data-testid="input-branch-name-ar"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setCreateDialog(false); setFormData({ name_en: '', name_ar: '' }); }}
              data-testid="cancel-create-branch"
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={submitting || !formData.name_en.trim()}
              data-testid="submit-create-branch"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRTL ? 'إنشاء' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, branch: null })}>
        <DialogContent className="max-w-md" data-testid="edit-branch-dialog">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تعديل الفرع' : 'Edit Branch'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (إنجليزي) *' : 'Name (English) *'}</Label>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                data-testid="input-edit-branch-name-en"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (عربي)' : 'Name (Arabic)'}</Label>
              <Input
                value={formData.name_ar}
                onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                dir="rtl"
                data-testid="input-edit-branch-name-ar"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setEditDialog({ open: false, branch: null }); setFormData({ name_en: '', name_ar: '' }); }}
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleUpdate} 
              disabled={submitting || !formData.name_en.trim()}
              data-testid="submit-edit-branch"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRTL ? 'حفظ' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, branch: null })}>
        <AlertDialogContent data-testid="delete-branch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isRTL ? 'حذف الفرع' : 'Delete Branch'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isRTL 
                ? `هل أنت متأكد من حذف "${deleteDialog.branch?.name_en}"؟ سيتم إزالة الفرع من جميع الموظفين المرتبطين به.`
                : `Are you sure you want to delete "${deleteDialog.branch?.name_en}"? This branch will be removed from all associated employees.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-branch">
              {isRTL ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
              data-testid="confirm-delete-branch"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRTL ? 'حذف' : 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BranchesPage;
